import type { PersonProfileFields } from '@/lib/person-profile';
import type { InstantlyLeadStatus } from '@/types/search-filters';
// store/features/prospects.feature.tsx
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export interface ProspectQueryResult extends PersonProfileFields {
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  headline?: string;
  profile_pic_url?: string;
  current_company_name?: string;
  current_company_domain?: string;
  city?: string;
  state?: string;
  country?: string;
  country_full_name?: string;
  public_identifier?: string;
  public_linkedin_url?: string;
  has_email: boolean;
  has_phone: boolean;
  is_enriched: boolean;
  connections?: number;
  follower_count?: number;
}

export interface ProspectSearchResponse {
  results: ProspectQueryResult[];
  total: number;
  skip: number;
  limit: number;
  query: string;
}

export interface ProspectSearchParams {
  query: string;
  skip?: number;
  limit?: number;
}

export interface DatabaseProspectEnrichmentRequest {
  enrichment_type: 'partial' | 'full';
  lead_list_id: string;
  prospect_ids: string[];
}

export interface DatabaseProspectEnrichmentResponse {
  status: string;
  added_count: number;
  already_in_list_count: number;
  credits_used: number;
  total_leads_in_list: number;
  lead_list_id: string;
}

export interface RefreshProspectsRequest {
  prospect_ids: string[];
}

export interface RefreshProspectsResponse {
  success: boolean;
  message: string;
  total_requested: number;
  successful: number;
  failed: number;
  credits_deducted: number;
  results: Array<{
    prospect_id: string;
    status: 'success' | 'failed';
    full_name?: string;
    error?: string;
  }>;
}

// People Finder Types (Crustdata v2 /person/search, 2025-11-01)
export type CrustdataFilterOp =
  | '='
  | '!='
  | '>'
  | '<'
  | 'in'
  | 'not_in'
  | '(.)'
  | 'geo_distance';

export interface CrustdataCondition {
  field: string;
  type: CrustdataFilterOp;
  value: unknown;
}

export interface CrustdataFilterGroup {
  op: 'and' | 'or';
  conditions: (CrustdataCondition | CrustdataFilterGroup)[];
}

export interface CrmIdentityExclusionReference {
  crm: 'salesforce' | 'hubspot';
  condition: string;
  generation_id: string;
}

export interface CleanlistIdentityExclusionReference {
  scope: 'any' | 'list';
  list_ids?: string[];
}

/** Skip people already in the org's Instantly workspace (cleanse-api
 *  ``InstantlyIdentityExclusionReference``). ``statuses`` only with
 *  ``mode: 'statuses'``; ``mode: 'off'`` only with ``blocklist``. */
export interface InstantlyIdentityExclusionReference {
  mode: 'off' | 'any_campaign' | 'statuses';
  statuses?: InstantlyLeadStatus[];
  blocklist?: boolean;
}

/** The kinds the server can apply, as ``applied_identity_exclusions`` names them. */
export type IdentityExclusionKind = 'crm' | 'cleanlist' | 'instantly';

export interface PeopleIdentityExclusions {
  intent_token: string;
  crm: CrmIdentityExclusionReference[];
  cleanlist?: CleanlistIdentityExclusionReference;
  instantly?: InstantlyIdentityExclusionReference;
}

/**
 * Sort entry shape. Crustdata v1 persondb/search expects ``column``
 * (see docs/plans/crustdata-people-search-api.md). The previous
 * ``field`` key was v2-specific and got silently dropped upstream.
 */
export interface CrustdataSort {
  column: string;
  order: 'asc' | 'desc';
}

export interface PeopleSearchRequest {
  filters: CrustdataFilterGroup;
  fields?: string[];
  sorts?: CrustdataSort[];
  limit?: number;
  limit_per_company?: number;
  cursor?: string;
  identity_exclusions?: PeopleIdentityExclusions;
  /** Run the search as a background job (returns a task_id to poll via
   *  getPeopleSearchStatus) instead of blocking inline. */
  async_search?: boolean;
}

export interface PersonRecord {
  id?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  title?: string;
  company_name?: string;
  seniority_level?: string;
  company_domain?: string;
  linkedin_url?: string;
  profile_image_url?: string;
  headline?: string;
  email?: string;
  /** Hydrated server-side from leads the current user has previously
   *  enriched. Drives inline rendering on re-search (a row the user
   *  already paid credits on shouldn't render an Enrich button). */
  phone?: string;
  location?: string;
  /** Org-level presence, hydrated server-side: the matched lead's id and
   *  every org list already containing this person. Drives the extension's
   *  in-Cleanlist / in-this-list row states and keys the CRM sync-status
   *  batch lookup. Presence only — contact values stay user-scoped. */
  lead_id?: string | null;
  lead_list_ids?: string[] | null;
  /** Client-only: a reveal of this field found nothing. Kept in the saved
   *  search so "Not found" survives leaving the page instead of offering a
   *  reveal that already came back empty. */
  email_not_found?: boolean;
  phone_not_found?: boolean;
  raw: Record<string, unknown>;
}

/** Outcome of a search that asked each company separately: a per-company
 *  cap over several companies (backend ``_per_company_search``). */
export interface CompanyCoverage {
  /** Distinct companies with at least one person in the results. */
  companies_matched: number;
  /** Companies, as the search named them, where nobody matched. */
  no_match: string[];
  /** Companies whose search failed or ran out of time. */
  not_searched: string[];
}

export interface PeopleSearchResponse {
  results: PersonRecord[];
  total_count?: number;
  next_cursor?: string;
  applied_identity_exclusion_token?: string;
  /** The identity exclusion kinds the server compiled and applied. */
  applied_identity_exclusions?: IdentityExclusionKind[] | null;
  /** Instantly leads with no LinkedIn URL, which rows can't be checked
   *  against before reveal. Set when the Instantly exclusion ran a mode. */
  instantly_unchecked?: number | null;
  company_coverage?: CompanyCoverage | null;
  /** "fuzzy_name" when an exact name lookup found nobody and these rows are
   *  the closest names instead. Absent = exact. */
  match_mode?: 'fuzzy_name' | null;
  /** Async search: set on the start response and each poll.
   *  status = processing | completed | failed. */
  task_id?: string;
  status?: string;
  /** A failed job's error detail + HTTP status (e.g. the search admission
   *  402/403), which the inline call would have rejected with. */
  error?: string | Record<string, unknown>;
  status_code?: number;
}

export interface AutocompleteRequest {
  field: string;
  query: string;
  limit?: number;
}

export interface AutocompleteResponse {
  suggestions: string[];
}

export interface ImportPeopleRequest {
  lead_list_id: string;
  people: Record<string, unknown>[];
  /**
   * Optional enrichment trigger. When set, the backend kicks off a
   * BulkEnrichmentWorkflow against the imported leads so inline
   * "Enrich email" / "Enrich phone" buttons on the search table
   * actually resolve contact info.
   *  - "partial"   → work email (1 credit)
   *  - "phone_only"→ mobile phone (10 credits)
   *  - "full"      → email + phone
   *  - "none" | undefined → plain import, no enrichment
   */
  enrichment_type?: 'partial' | 'phone_only' | 'full' | 'none';
  /** Also enrich the requested people already on the list who lack what
   *  `enrichment_type` finds (Add to sequence enrolls them too). */
  enrich_listed?: boolean;
  /**
   * When true (the People search import always sends it), the import runs as a
   * background job: `/import` returns immediately with a `task_id` and
   * `status: 'processing'`, and the client polls `getPeopleImportStatus`. Keeps
   * large / enriched imports off the request's critical path so they can't hit
   * the gateway timeout (which surfaces as a CORS error). Omitted → synchronous.
   */
  async_import?: boolean;
}

/** What became of one requested person, in request order (additive). */
export interface PersonImportOutcome {
  index: number;
  linkedin_url: string | null;
  lead_id: string | null;
  outcome:
    | 'added'
    | 'already_in_list'
    | 'in_batch_duplicate'
    | 'not_added'
    | 'error';
  error: string | null;
}

export interface ImportPeopleResponse {
  success: boolean;
  imported_count: number;
  skipped_count: number;
  errors: string[];
  lead_ids: string[];
  /** Requested people who were already on the list. */
  already_in_list_ids?: string[];
  /** One per requested person; absent from an API that predates it. */
  outcomes?: PersonImportOutcome[];
  // Async import. Null/undefined on the synchronous path. `status` is 'processing' | 'completed' | 'failed'.
  task_id?: string;
  status?: 'processing' | 'completed' | 'failed';
  total?: number;
  already_in_list_count?: number;
  in_batch_duplicate_count?: number;
}

export const prospectsApi = createApi({
  reducerPath: 'prospectsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ProspectSearch'],
  endpoints: (builder) => ({
    searchProspects: builder.query<
      ProspectSearchResponse,
      ProspectSearchParams
    >({
      query: ({ query, skip = 0, limit = 50 }) => ({
        url: '/prospects/query',
        params: { q: query, skip, limit },
      }),
      providesTags: (_, __, { query, skip, limit }) => [
        { type: 'ProspectSearch', id: `${query}-${skip}-${limit}` },
      ],
    }),

    enrichDatabaseProspects: builder.mutation<
      DatabaseProspectEnrichmentResponse,
      DatabaseProspectEnrichmentRequest
    >({
      query: (payload) => ({
        url: '/enrichment/database',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ProspectSearch' }],
    }),

    // Refresh prospects with live data
    refreshProspects: builder.mutation<
      RefreshProspectsResponse,
      RefreshProspectsRequest
    >({
      query: (payload) => ({
        url: '/prospects/refresh',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ProspectSearch' }],
    }),

    // People Finder - Crustdata integration
    peopleSearch: builder.mutation<PeopleSearchResponse, PeopleSearchRequest>({
      query: (payload) => ({
        url: '/people/find',
        method: 'POST',
        body: payload,
      }),
    }),

    // Import-only "fetch more" — byte-for-byte mirror of peopleSearch.
    // Keeps the import drawer's fetch separated from the on-screen
    // search preview so the two can diverge server-side.
    peopleImportFetch: builder.mutation<
      PeopleSearchResponse,
      PeopleSearchRequest
    >({
      query: (payload) => ({
        url: '/people/find/preview',
        method: 'POST',
        body: payload,
      }),
    }),

    // Autocomplete for people search filters - cached for 5 minutes
    // Request max 100 results (autocomplete is free) for better title expansion
    autocomplete: builder.query<string[], AutocompleteRequest>({
      query: ({ field, query, limit = 100 }) => ({
        url: '/people/find/filters',
        method: 'POST',
        body: { field, query, limit },
      }),
      transformResponse: (response: AutocompleteResponse) =>
        response.suggestions || [],
      // Cache results for 5 minutes - provides instant results on repeated searches
      keepUnusedDataFor: 300,
    }),

    // Import people from search results to a lead list
    importPeople: builder.mutation<ImportPeopleResponse, ImportPeopleRequest>({
      query: (payload) => ({
        url: '/people/find/bulk',
        method: 'POST',
        body: payload,
      }),
    }),

    // Poll the status of an async import job. Used imperatively via the lazy
    // hook: fire /import with async_import: true, then poll this until
    // status !== 'processing'.
    getPeopleImportStatus: builder.query<ImportPeopleResponse, string>({
      query: (taskId) => ({
        url: '/people/find/bulk/status',
        method: 'GET',
        params: { task_id: taskId },
      }),
    }),

    // Poll an async people-search job. Fire /search with async_search: true,
    // then poll this until status !== 'processing'.
    getPeopleSearchStatus: builder.query<PeopleSearchResponse, string>({
      query: (taskId) => ({
        url: `/people/find/status/${taskId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useRefreshProspectsMutation,
  usePeopleSearchMutation,
  usePeopleImportFetchMutation,
  useLazyAutocompleteQuery,
  useImportPeopleMutation,
  useLazyGetPeopleImportStatusQuery,
  useLazyGetPeopleSearchStatusQuery,
} = prospectsApi;
